import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import StoreProductCard, {
  type StoreProductCardData,
} from "@/components/store/StoreProductCard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Store, Sparkles, ShieldCheck, Search, X, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { APPS } from "@/lib/apps";

type Category = { id: string; slug: string; name: string; icon: string | null };

type SortKey = "newest" | "price_asc" | "price_desc" | "popular";
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low → High" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "popular", label: "Most popular" },
];

const PRICE_RANGES = [
  { value: "", label: "Any price" },
  { value: "0-1000", label: "Under $10" },
  { value: "1000-2500", label: "$10 – $25" },
  { value: "2500-5000", label: "$25 – $50" },
  { value: "5000-10000", label: "$50 – $100" },
  { value: "10000-", label: "$100+" },
];

/** Build the next URLSearchParams record, preserving only non-empty values. */
const buildParams = (obj: Record<string, string | null | undefined>) => {
  const next: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v) next[k] = v;
  }
  return next;
};

const StorePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeApp = searchParams.get("app");
  const queryParam = searchParams.get("q") ?? "";
  const sortParam = (searchParams.get("sort") as SortKey) || "newest";
  const priceParam = searchParams.get("price") ?? "";

  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<StoreProductCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(queryParam);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Helpers to update URL while preserving other params
  const currentBase = () => ({
    app: activeApp,
    q: queryParam || null,
    sort: sortParam !== "newest" ? sortParam : null,
    price: priceParam || null,
  });

  const commitSearch = (value: string) => {
    setSearchParams(buildParams({ ...currentBase(), q: value.trim() || null }));
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commitSearch(value), 350);
  };

  const clearSearch = () => {
    setSearchInput("");
    commitSearch("");
  };

  const setAppFilter = (appId: string | null) => {
    setSearchParams(buildParams({ ...currentBase(), app: appId }));
  };

  const setSort = (value: SortKey) => {
    setSearchParams(buildParams({ ...currentBase(), sort: value !== "newest" ? value : null }));
  };

  const setPriceRange = (value: string) => {
    setSearchParams(buildParams({ ...currentBase(), price: value || null }));
  };

  const clearAllFilters = () => {
    setSearchInput("");
    setSearchParams({});
  };

  const hasActiveFilters = !!(activeApp || queryParam || sortParam !== "newest" || priceParam);

  // Parse price range
  const [priceMin, priceMax] = (() => {
    if (!priceParam) return [null, null] as const;
    const [lo, hi] = priceParam.split("-");
    return [lo ? Number(lo) : null, hi ? Number(hi) : null] as const;
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      // Sort mapping
      const orderCol =
        sortParam === "price_asc" || sortParam === "price_desc"
          ? "base_price_cents"
          : sortParam === "popular"
            ? "purchase_count"
            : "created_at";
      const ascending = sortParam === "price_asc";

      const prodQuery = supabase
        .from("store_products")
        .select(
          "id, slug, title, base_price_cents, currency, images, app_id, vendor:store_vendors_public!vendor_id(slug, display_name)",
        )
        .eq("status", "active")
        .order(orderCol, { ascending })
        .limit(24);

      if (activeApp) prodQuery.eq("app_id", activeApp);
      if (queryParam) prodQuery.ilike("title", `%${queryParam}%`);
      if (priceMin !== null) prodQuery.gte("base_price_cents", priceMin);
      if (priceMax !== null) prodQuery.lte("base_price_cents", priceMax);

      const [{ data: cats }, { data: prods }] = await Promise.all([
        supabase
          .from("store_categories")
          .select("id, slug, name, icon")
          .eq("active", true)
          .order("sort_order"),
        prodQuery,
      ]);
      if (cancelled) return;
      setCategories((cats as Category[]) ?? []);
      setProducts((prods as unknown as StoreProductCardData[]) ?? []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [activeApp, queryParam, sortParam, priceParam]);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border/60">
          <div className="absolute inset-0 bg-wavlynq-gradient opacity-[0.06] pointer-events-none" />
          <div className="relative mx-auto w-full max-w-7xl px-6 py-12">
            <div className="flex items-center gap-3 text-primary">
              <Store className="h-6 w-6" />
              <span className="text-xs uppercase tracking-widest font-semibold">
                Curated marketplace
              </span>
            </div>
            <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight max-w-2xl">
              Goods from the people who actually live in our communities.
            </h1>
            <p className="mt-4 max-w-xl text-muted-foreground">
              Hand-picked vendors. Real makers. Every order helps fund community
              moderation, safety, and mentor stipends.
            </p>
            {/* Search box */}
            <div className="mt-6 relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder={activeApp ? `Search ${APPS.find((a) => a.id === activeApp)?.name ?? "community"} products…` : "Search all products…"}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </section>

        {/* App filter chips */}
        <section className="mx-auto w-full max-w-7xl px-6 pt-8 pb-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Shop by community
          </h2>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAppFilter(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                !activeApp
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              }`}
            >
              All
            </button>
            {APPS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setAppFilter(a.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                  activeApp === a.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                }`}
              >
                <img src={a.icon} alt="" className="h-4 w-4 rounded-sm" />
                {a.name}
              </button>
            ))}
          </div>
        </section>

        {/* Sort & filter bar */}
        <section className="mx-auto w-full max-w-7xl px-6 pt-4 pb-2">
          <div className="flex flex-wrap items-center gap-3">
            {/* Sort */}
            <Select value={sortParam} onValueChange={(v) => setSort(v as SortKey)}>
              <SelectTrigger className="w-[180px] h-9 text-sm">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price range */}
            <Select value={priceParam || "__any"} onValueChange={(v) => setPriceRange(v === "__any" ? "" : v)}>
              <SelectTrigger className="w-[160px] h-9 text-sm">
                <SelectValue placeholder="Any price" />
              </SelectTrigger>
              <SelectContent>
                {PRICE_RANGES.map((r) => (
                  <SelectItem key={r.value || "__any"} value={r.value || "__any"}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Toggle extra filters (mobile) */}
            <Button
              variant="outline"
              size="sm"
              className="sm:hidden"
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              <SlidersHorizontal className="h-4 w-4 mr-1.5" />
              Filters
            </Button>

            {/* Clear all */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs text-muted-foreground hover:text-foreground underline ml-auto"
              >
                Clear all filters
              </button>
            )}
          </div>
        </section>

        {categories.length > 0 && (
          <section className="mx-auto w-full max-w-7xl px-6 py-8">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Categories
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {categories.map((c) => (
                <Link key={c.id} to={`/store/category/${c.slug}`}>
                  <Card className="p-4 text-center transition-colors hover:bg-muted/50">
                    <p className="font-medium text-sm">{c.name}</p>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mx-auto w-full max-w-7xl px-6 py-8">
          <h2 className="text-xl font-semibold mb-4">
            {queryParam
              ? `Results for "${queryParam}"${activeApp ? ` · ${APPS.find((a) => a.id === activeApp)?.name ?? activeApp}` : ""}`
              : activeApp
                ? `New arrivals · ${APPS.find((a) => a.id === activeApp)?.name ?? activeApp}`
                : "New arrivals"}
          </h2>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-lg" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <Card className="p-12 text-center">
              <Sparkles className="h-10 w-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-lg">
                {hasActiveFilters ? "No products match your filters" : "Vendors are setting up shop"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {hasActiveFilters ? (
                  <button type="button" onClick={clearAllFilters} className="underline">
                    Clear all filters
                  </button>
                ) : (
                  <>
                    The first listings will appear here as vendors finish onboarding.
                    Check back soon — or{" "}
                    <Link to="/partner-access#apply" className="underline">
                      apply to sell
                    </Link>
                    .
                  </>
                )}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((p) => (
                <StoreProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </section>

        <section className="mx-auto w-full max-w-7xl px-6 py-12 grid gap-4 md:grid-cols-3">
          <Card className="p-6">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <h3 className="mt-3 font-semibold">Verified vendors</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Every seller is identity-verified and reviewed before listing.
            </p>
          </Card>
          <Card className="p-6">
            <Sparkles className="h-6 w-6 text-primary" />
            <h3 className="mt-3 font-semibold">14-day refund window</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Refunds under $25 are auto-approved. Wavlynq backs your purchase.
            </p>
          </Card>
          <Card className="p-6">
            <Store className="h-6 w-6 text-primary" />
            <h3 className="mt-3 font-semibold">Physical &amp; digital</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Handcrafted goods, art, music, and digital downloads — all in one cart.
            </p>
          </Card>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default StorePage;
